import React, { useState } from 'react';
import { Card, Nav, Table, Badge, Form, Row, Col } from 'react-bootstrap';
import { FaEye, FaEdit, FaTrash, FaPlus, FaSave, FaUpload } from 'react-icons/fa';
import './MaintenanceManagement.scss';

const MaintenanceManagement = () => {
  const [key, setKey] = useState('requests');

  // Sample maintenance requests data
  const [requests] = useState([
    {
      id: 1,
      property: 'Luxury Villa A12',
      tenant: 'John Smith',
      type: 'Plumbing',
      issue: 'Plumbing Issue',
      description: 'Leaking faucet in master bathroom',
      status: 'Pending',
      priority: 'High',
      date: '2025-04-06',
      preferredVisitDate: '2025-04-08',
      img: '/assets/images/maintenance/plumbing1.jpg'
    },
    {
      id: 2,
      property: 'Apartment B45',
      tenant: 'Sarah Johnson',
      type: 'Electrical',
      issue: 'Electrical Issue',
      description: 'Power outlet not working in kitchen',
      status: 'In Progress',
      priority: 'Medium',
      date: '2025-04-05',
      preferredVisitDate: '2025-04-07',
      img: '/assets/images/maintenance/electrical1.jpg'
    }
  ]);

  // Sample additional services data
  const [services, setServices] = useState([
    {
      id: 1,
      service: 'Regular Cleaning',
      description: 'Weekly cleaning service for common areas',
      price: 150,
      frequency: 'Weekly',
      img: '/assets/images/services/cleaning.jpg'
    },
    {
      id: 2,
      service: 'Pool Maintenance',
      description: 'Monthly pool cleaning and maintenance',
      price: 200,
      frequency: 'Monthly',
      img: '/assets/images/services/pool.jpg'
    }
  ]);

  // Sample request tracking data
  const [tracking] = useState([
    {
      id: 1,
      requestId: 'REQ-001',
      property: 'Luxury Villa A12',
      status: 'Completed',
      assignedTo: 'Mike Wilson',
      startDate: '2025-04-01',
      completionDate: '2025-04-03',
      cost: 250
    },
    {
      id: 2,
      requestId: 'REQ-002',
      property: 'Apartment B45',
      status: 'In Progress',
      assignedTo: 'David Brown',
      startDate: '2025-04-05',
      completionDate: null,
      cost: 0
    }
  ]);

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'in progress':
        return 'info';
      case 'completed':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const handleAddService = () => {
    setServices([...services, {
      id: Date.now(),
      service: '',
      description: '',
      price: 0,
      frequency: '',
      img: '/assets/images/placeholder.jpg'
    }]);
  };

  const handleDeleteService = (id) => {
    setServices(services.filter(service => service.id !== id));
  };

  const handleImageUpload = (id, e) => {
    const file = e.target.files[0];
    if (file) {
      // Handle image upload - implement actual upload logic
      console.log(`Uploading image for service ${id}:`, file);
    }
  };

  return (
    <div className="maintenance-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title">Maintenance Management</h2>
      </div>

      <Nav variant="tabs" activeKey={key} onSelect={(k) => setKey(k)}>
        <Nav.Item>
          <Nav.Link eventKey="requests">Maintenance Requests</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="services">Additional Services</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="tracking">Request Tracking</Nav.Link>
        </Nav.Item>
      </Nav>

      {key === 'requests' && (
        <Card className="content-card mt-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Card.Title>Maintenance Requests</Card.Title>
              <div className="summary-badge">
                Total: {requests.length} requests
              </div>
            </div>
            <Table responsive>
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Property</th>
                  <th>Tenant</th>
                  <th>Type</th>
                  <th>Issue</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Date</th>
                  <th>Preferred Visit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <div className="table-image-container">
                        <img src={request.img} alt={request.issue} className="table-image" />
                        <label className="image-upload-overlay">
                          <FaUpload />
                          <Form.Control
                            type="file"
                            accept="image/*"
                            className="d-none"
                            onChange={(e) => {
                              // Handle image upload
                              console.log(`Uploading image for request ${request.id}:`, e.target.files[0]);
                            }}
                          />
                        </label>
                      </div>
                    </td>
                    <td>{request.property}</td>
                    <td>{request.tenant}</td>
                    <td>
                      <Badge bg="secondary" className="type-badge">
                        {request.type}
                      </Badge>
                    </td>
                    <td>{request.issue}</td>
                    <td className="description-cell">{request.description}</td>
                    <td>
                      <Badge bg={getStatusBadgeClass(request.status)}>
                        {request.status}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={getPriorityBadgeClass(request.priority)}>
                        {request.priority}
                      </Badge>
                    </td>
                    <td>{request.date}</td>
                    <td>{request.preferredVisitDate}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-info btn-sm">
                          <FaEye />
                        </button>
                        <button className="btn btn-primary btn-sm">
                          <FaEdit />
                        </button>
                        <button className="btn btn-danger btn-sm">
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {key === 'services' && (
        <Card className="content-card mt-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Card.Title>Additional Maintenance Services</Card.Title>
              <button className="btn btn-primary" onClick={handleAddService}>
                <FaPlus className="me-2" />
                Add Service
              </button>
            </div>
            <Table responsive>
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Service</th>
                  <th>Description</th>
                  <th>Price ($)</th>
                  <th>Frequency</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.map((service) => (
                  <tr key={service.id}>
                    <td>
                      <div className="table-image-container">
                        <img src={service.img} alt={service.service} className="table-image" />
                        <label className="image-upload-overlay">
                          <FaUpload />
                          <Form.Control
                            type="file"
                            accept="image/*"
                            className="d-none"
                            onChange={(e) => handleImageUpload(service.id, e)}
                          />
                        </label>
                      </div>
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={service.service}
                        onChange={(e) => {
                          const newServices = services.map(s =>
                            s.id === service.id ? { ...s, service: e.target.value } : s
                          );
                          setServices(newServices);
                        }}
                        placeholder="Enter service name"
                      />
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={service.description}
                        onChange={(e) => {
                          const newServices = services.map(s =>
                            s.id === service.id ? { ...s, description: e.target.value } : s
                          );
                          setServices(newServices);
                        }}
                        placeholder="Enter description"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        value={service.price}
                        onChange={(e) => {
                          const newServices = services.map(s =>
                            s.id === service.id ? { ...s, price: parseFloat(e.target.value) } : s
                          );
                          setServices(newServices);
                        }}
                        placeholder="Enter price"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={service.frequency}
                        onChange={(e) => {
                          const newServices = services.map(s =>
                            s.id === service.id ? { ...s, frequency: e.target.value } : s
                          );
                          setServices(newServices);
                        }}
                        placeholder="Enter frequency"
                      />
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteService(service.id)}>
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex justify-content-end mt-4">
              <button className="btn btn-primary">
                <FaSave className="me-2" />
                Save Changes
              </button>
            </div>
          </Card.Body>
        </Card>
      )}

      {key === 'tracking' && (
        <Card className="content-card mt-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Card.Title>Request Tracking</Card.Title>
              <div className="summary-badge">
                Total: {tracking.length} requests
              </div>
            </div>
            <Table responsive>
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>Property</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Start Date</th>
                  <th>Completion Date</th>
                  <th>Cost ($)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tracking.map((track) => (
                  <tr key={track.id}>
                    <td>{track.requestId}</td>
                    <td>{track.property}</td>
                    <td>
                      <Badge bg={getStatusBadgeClass(track.status)}>
                        {track.status}
                      </Badge>
                    </td>
                    <td>{track.assignedTo}</td>
                    <td>{track.startDate}</td>
                    <td>{track.completionDate || '-'}</td>
                    <td>${track.cost}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-info btn-sm">
                          <FaEye />
                        </button>
                        <button className="btn btn-primary btn-sm">
                          <FaEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default MaintenanceManagement;
